import React from "react";
import { AppBar, Toolbar, Typography, IconButton, Tooltip, Avatar, Box } from "@mui/material";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";
import { logout } from "../services/authService";
import { tokens } from "../theme/tokens";

interface FancyAppBarProps {
    /** Shown on mobile only — toggles the nav drawer. */
    onMenuClick?: () => void;
}

// ---------- COLORS (flat navy + white) ----------
const COLORS = {
    appBarBg: tokens.color.brandDark,   // solid deep navy, no gradient
    shadow: "0 1px 4px rgba(15,23,42,0.18)",
    brandText: "#fff",
    iconBg: tokens.color.brandAccent,
    logoutIcon: "rgba(255,255,255,0.16)",
    logoutHover: "#fff",
    appBarHeight: 64,
};

const FancyAppBar: React.FC<FancyAppBarProps> = ({ onMenuClick }) => {
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    return (
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: COLORS.appBarBg,
                    boxShadow: COLORS.shadow,
                    height: COLORS.appBarHeight,
                    zIndex: 1300,
                }}
            >
                <Toolbar sx={{ minHeight: COLORS.appBarHeight, position: "relative" }}>
                    {onMenuClick && (
                        <IconButton
                            aria-label="Open navigation menu"
                            onClick={onMenuClick}
                            sx={{
                                display: { xs: "inline-flex", md: "none" },
                                position: "absolute",
                                left: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: COLORS.brandText,
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Avatar
                        sx={{
                            bgcolor: COLORS.iconBg,
                            width: 42,
                            height: 42,
                            boxShadow: COLORS.shadow,
                            position: "absolute",
                            left: 18,
                            top: "50%",
                            transform: "translateY(-50%)",
                            display: { xs: "none", md: "flex" },
                        }}
                    >
                        <DirectionsCarFilledIcon sx={{ fontSize: 25, color: COLORS.brandText }} />
                    </Avatar>
                    <Box sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "100%",
                        textAlign: "center",
                        pointerEvents: "none"
                    }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 800,
                                color: COLORS.brandText,
                                letterSpacing: 1,
                                fontSize: { xs: 18, md: 22 }
                            }}
                        >
                            SwiftaGo Admin
                        </Typography>
                    </Box>
                    <Box sx={{
                        position: "absolute",
                        right: 18,
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}>
                        <Tooltip title="Logout">
                            <IconButton
                                sx={{
                                    bgcolor: COLORS.logoutIcon,
                                    color: COLORS.logoutHover,
                                    transition: "0.2s",
                                    "&:hover": {
                                        bgcolor: COLORS.brandText,
                                        color: COLORS.appBarBg,
                                    },
                                }}
                                onClick={handleLogout}
                            >
                                <LogoutIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </AppBar>
    );
};

export default FancyAppBar;
export const APP_BAR_HEIGHT = COLORS.appBarHeight;
